import Phaser from 'phaser';
import { CATCH_LINE_Y, INTERNAL_WIDTH, PERFECT_POSITION_FRACTION, TIMING_WINDOWS } from '@/game/config/GameConfig';
import { DEBUG_QUERY_PARAM } from '@/game/config/GameConfig';

export function isDebugMode(): boolean {
  try {
    const params = new URLSearchParams(window.location.search);
    return params.get(DEBUG_QUERY_PARAM) === 'true';
  } catch {
    return false;
  }
}

export interface DebugFrameInfo {
  songTime: number;
  beat: number;
  bpm: number;
  fps: number;
  audioOffsetMs: number;
  activeNotes: number;
  songProgress: number;
}

/** Toggleable dev overlay (`?debug=true`) showing rhythm-engine internals and the
 * hit-window/catch-zone geometry, for chart authoring and sync verification. */
export class DebugOverlay {
  private text: Phaser.GameObjects.Text;
  private graphics: Phaser.GameObjects.Graphics;

  constructor(private scene: Phaser.Scene) {
    this.graphics = scene.add.graphics().setDepth(999);
    this.text = scene.add
      .text(8, 8, '', {
        fontFamily: 'monospace',
        fontSize: '16px',
        color: '#00ff88',
        backgroundColor: '#000000aa',
        padding: { x: 6, y: 4 },
      })
      .setDepth(1000);

    this.drawStaticGuides();
  }

  private drawStaticGuides(): void {
    this.graphics.lineStyle(2, 0x00ff88, 0.8);
    this.graphics.lineBetween(0, CATCH_LINE_Y, INTERNAL_WIDTH, CATCH_LINE_Y);
  }

  update(info: DebugFrameInfo): void {
    this.text.setText(
      [
        `song time: ${info.songTime.toFixed(3)}s`,
        `progress:  ${(info.songProgress * 100).toFixed(1)}%`,
        `beat:      ${info.beat.toFixed(2)} (bpm ${info.bpm})`,
        `fps:       ${info.fps.toFixed(0)}`,
        `audioOffset: ${info.audioOffsetMs.toFixed(0)}ms`,
        `active notes: ${info.activeNotes}`,
        `catch window: \xb1${TIMING_WINDOWS.goodMs}ms (else miss)`,
        `perfect zone: within ${(PERFECT_POSITION_FRACTION * 100).toFixed(0)}% of catch center`,
      ].join('\n')
    );
  }

  destroy(): void {
    this.text.destroy();
    this.graphics.destroy();
  }
}
